import {createRouter, createWebHistory} from 'vue-router';
import {useAuthStore} from '@/stores/authStore';
import {useDialogStore} from "@/stores/dialogStore.js";

const filterPath = (to, from, next) => {
  if (to.fullPath !== to.path) {
    next({ path: to.path });
  } else {
    next();
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    } else {
      return { top: 0, behavior: 'smooth' };
    } 
  },
  routes: [
    {
      path: '/',
      name: 'entrypage',
      meta: {
        title: 'Managed Care Systems Portal',
        requiresAuth: false,
      },
      component: () => import('@/views/EntryPage.vue'),
      beforeEnter: (to, from , next) => {
        const store = useAuthStore();
        if (store.authenticated) {
          next({ path: '/MainLandingPage' });
        } else {
          filterPath(to, from ,next);
        }
      }
    },
    {
      path: '/SignInRegister',
      name: 'signinregister',
      meta: {
        title: 'Sign In or Register',
      },
      component: () => import('@/components/registration/SignInRegister.vue'),
      beforeEnter: (to, from , next) => {
        const store = useAuthStore();
        if (store.authenticated) {
          next({ path: '/MainLandingPage' });
        } else {
          filterPath(to, from ,next);
        }
      }
    },
    {
      path: '/appealsAndComplaints',
      name: 'AppealsAndComplaints',
      meta: {
        title: 'Appeals and Complaints',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/AppealsAndComplaints.vue')
    },
    {
      path: '/appealsAndComplaints/FileAnExternalAppeal',
      name: 'FileAnExternalAppeal',
      meta: {
        title: 'File an External Appeal',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints', route: '/AppealsAndComplaints' },
          { label: 'File An External Appeal' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/FileAnExternalAppeal.vue')
    },
    {
      path: '/appealsAndComplaints/ExternalAppealForm',
      name: 'ExternalAppealForm',
      meta: {
        title: 'External Appeal Form',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints', route: '/appealsAndComplaints' },
          { label: 'File An External Appeal', route: '/appealsAndComplaints/FileAnExternalAppeal' },
          { label: 'External Appeal Form' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/ExternalAppealForm.vue')
    },
    {
      path: '/appealsAndComplaints/FileAConsumerComplaint',
      name: 'FileAConsumerComplaint',
      meta: {
        title: 'File A Consumer Complaint',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints', route: '/appealsAndComplaints' },
          { label: 'File A Consumer Complaint' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/FileAConsumerComplaint.vue')
    },
    {
      path: '/appealsAndComplaints/ConsumerComplaintForm',
      name: 'ConsumerComplaintForm',
      meta: {
        title: 'Consumer Complaint Form',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints', route: '/appealsAndComplaints' },
          { label: 'File A Consumer Complaint', route: '/appealsAndComplaints/FileAConsumerComplaint' },
          { label: 'Consumer Complaint Form' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/ConsumerComplaintForm.vue')
    },
    {
      path: '/appealsAndComplaints/CheckStatusAppealComplaint',
      name: 'CheckStatusAppealComplaint',
      meta: {
        title: 'Check Status of an Appeal or Consumer Complaint',
        requiresAuth: false,
        breadcrumb: [
          { label: 'Appeals and Complaints', route: '/appealsAndComplaints' },
          { label: 'Check Status of an Appeal or Consumer Complaint' }
        ],
      },
      component: () => import('@/views/appealsAndComplaints/CheckStatusAppealComplaint.vue')
    },
    {
      path: '/MainLandingPage',
      name: 'MainLandingPage',
      meta: {
        title: 'Main Landing Page',
        requiresAuth: true
      },
      beforeEnter: filterPath,
      component: () => import('@/views/MainLandingPage.vue'),
    },
    {
      path: '/ManageAccount',
      name: 'ManageAccount',
      meta: {
        title: 'Manage Account',
        requiresAuth: true
      },
      component: () => import('@/views/manageAccount/ManageAccountView.vue')
    },
    {
      path: '/Admin/NetworksAwaitingReview',
      name: 'Networks Awaiting Review',
      meta: {
        title: 'Networks Awaiting Review',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'NetworkAdequacy'],
        breadcrumb: [
          { label: 'Networks Awaiting Review' }
        ],
      },
      component: () => import('@/views/admin/NetworksAwaitingReview.vue'),
    },
    {
      path: '/Admin/AdminUser',
      name: 'Admin Users View',
      meta: {
        title: 'Admin Users View',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Admin Users View' }
        ],
      },
      component: () => import('@/views/admin/AdminUserView.vue'),
    },
    {
      path: '/Admin/AdminEntity',
      name: 'Admin Entity View',
      meta: {
        title: 'Admin Entity View',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Admin Entity View' }
        ],
      },
      component: () => import('@/views/admin/AdminEntityView.vue'),
    },
    {
      path: '/Admin/AdminAuditLog',
      name: 'Admin Audit Log',
      meta: {
        title: 'Admin Audit Log',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Admin Audit Log' }
        ],
      },
      component: () => import('@/views/admin/AdminAuditLog.vue'),
    },
    {
      path: '/Admin/AdminReviewEntities',
      name: 'Admin Review Entities',
      meta: {
        title: 'Admin Review Entities',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'NetworkAdequacy'],
        breadcrumb: [
          { label: 'Admin Review Entities' }
        ],
      },
      component: () => import('@/views/admin/AdminReviewEntities.vue'),
    },
    {
      path: '/Admin/AdminReviewNetworks',
      name: 'Admin Review Networks',
      meta: {
          title: 'Admin Review Networks',
          requiresAuth: true,
          requiresRoles: ['ADMIN', 'NetworkAdequacy'],
          breadcrumb: [
              { label: 'Admin Review Networks' }
          ],
      },
      component: () => import('@/views/admin/AdminReviewNetworks.vue'),
    },
    {
      path: '/Admin/AdminExternalAppeals',
      name: 'Admin External Appeals',
      meta: {
        title: 'Admin External Appeals',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'Investigator'],
        breadcrumb: [
          { label: 'Admin External Appeals' }
        ],
      },
      component: () => import('@/views/admin/AdminExternalAppeals.vue'),
    },
    {
      path: '/pdfGenFromHtmlPages/ViewSaveAsPdfExternalAppeal',
      name: 'ViewSaveAsPdfExternalAppeal',
      meta: {
        title: 'View Save As PDF External Appeal',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'Investigator'],
        breadcrumb: [
          { label: 'Admin External Appeals' , route: '/Admin/AdminExternalAppeals'},
          { label: 'PDF View and Save Appeals'}
        ],
      },
      component: () => import('@/views/pdfGenFromHtmlPages/ViewSaveAsPdfExternalAppeal.vue'),
    },
    {
      path: '/Admin/AdminConsumerComplaints',
      name: 'Admin Consumer Complaints',
      meta: {
        title: 'Admin Consumer Complaints',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'Investigator'],
        breadcrumb: [
          { label: 'Admin Consumer Complaints' }
        ],
      },
      component: () => import('@/views/admin/AdminConsumerComplaints.vue'),
    },
    {
      path: '/Admin/EntityAdminUsers',
      name: 'Entity Admin Users',
      meta: {
        title: 'My Entity Users',
        requiresAuth: true,
        requiresRoles: ['EntityAdministrator', 'ADMIN'],
        breadcrumb: [
          { label: 'My Entity Users' }
        ],
      },
      component: () => import('@/views/admin/EntityAdminUsersView.vue'),
    },
    {
      path: '/Admin/EntityClaimRequests',
      name: 'Entity Claim Requests',
      meta: {
        title: 'Entity Claim Requests',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA', 'NetworkAdequacy', 'Investigator'],
        // Reviewed by MDH staff generally rather than by one role's area, so it is pinned to the
        // Administration section instead of being bucketed by its requiresRoles.
        menuSection: 'admin',
        breadcrumb: [
          { label: 'Entity Claim Requests' }
        ],
      },
      component: () => import('@/views/admin/EntityClaimRequestsView.vue'),
    },
    {
      path: '/Admin/EntityAdminUserRequests',
      name: 'Entity Admin User Requests',
      meta: {
        title: 'Entity Admin User Requests',
        requiresAuth: true,
        requiresRoles: ['ADMIN'],
        breadcrumb: [
          { label: 'Entity Admin User Requests' }
        ],
      },
      component: () => import('@/views/admin/EntityAdminUserRequestsView.vue'),
    },
    {
      path: '/pdfGenFromHtmlPages/ViewSaveAsPdfConsumerComplaint',
      name: 'ViewSaveAsPdfConsumerComplaint',
      meta: {
        title: 'View Save As PDF Consumer Complaint',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'Investigator'],
        breadcrumb: [
          { label: 'Admin Consumer Complaints' , route: '/Admin/AdminConsumerComplaints'},
          { label: 'PDF View and Save Complaint'}
        ],
      },
      component: () => import('@/views/pdfGenFromHtmlPages/ViewSaveAsPdfConsumerComplaint.vue'),
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      meta: {
        title: 'Unauthorized'
      },
      beforeEnter: filterPath,
      component: () => import('../views/UnauthorizedView.vue')
    },    
    {
      path: '/:catchAll(.*)',
      name: 'notfound',
      component: () => import('../components/NotFound.vue'),
      meta: {
        title: 'Not Found',
      },
      beforeEnter: filterPath
    },
// Begin - Network Adequacy
    // TODO: Noah- after more functionality gets added to these pages it may make more sense to break out into a module.
    {
      path: '/NetworkAdequacy',
      name: 'NetworkAdequacy',
      meta: {
        title: 'Network Adequacy',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy' }
        ],
      },
      component: () => import('@/views/networkAdequacy/NetworkAdequacy.vue'),
    },
    {
      path: '/NetworkAdequacy/AnnualNetworkReview',
      name: 'AnnualNetworkReview',
      meta: {
        title: 'Annual Network Review for Upcoming Plan Year',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy', route: '/NetworkAdequacy' },
          { label: 'Annual Network Review for Upcoming Plan Year' }
        ],
      },
      component: () => import('@/views/networkAdequacy/AnnualNetworkReview.vue'),
    },
    {
      path: '/NetworkAdequacy/ProviderTerminations',
      name: 'ProviderTerminations',
      meta: {
        title: 'Provider Terminations',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy', route: '/NetworkAdequacy' },
          { label: 'Provider Terminations' }
        ],
      },
      component: () => import('@/views/networkAdequacy/ProviderTerminations.vue'),
    },
    {
      path: '/NetworkAdequacy/ProviderFileUpdates',
      name: 'ProviderFileUpdates',
      meta: {
        title: 'Provider File Updates',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy', route: '/NetworkAdequacy' },
          { label: 'Provider File Updates' }
        ],
      },
      component: () => import('@/views/networkAdequacy/ProviderFileUpdates.vue'),
    },
// End - Network Adequacy
// Begin - Network Adequacy for PBMs
    // TODO: Noah- after more functionality gets added to these pages it may make more sense to break out into a module.
    {
      path: '/NetworkAdequacyForPBMs',
      name: 'NetworkAdequacyForPBMs',
      meta: {
        title: 'Network Adequacy For PBMs',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/NetworkAdequacyForPBMs.vue'),
    },
    {
      path: '/NetworkAdequacyForPBMs/Attestation',
      name: 'Attestation',
      meta: {
        title: 'Network Adequacy Attestation',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs', route: '/NetworkAdequacyForPBMs' },
          { label: 'Attestation' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/Attestation.vue'),
    },
    {
      path: '/NetworkAdequacyForPBMs/GeographicAccessMap',
      name: 'Geographic Access Map',
      meta: {
        title: 'Geographic Access Map',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs', route: '/NetworkAdequacyForPBMs' },
          { label: 'Geographic Access Map' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/GeographicAccessMap.vue'),
    },
    {
      path: '/NetworkAdequacyForPBMs/NetworkWaiver',
      name: 'Network Waiver',
      meta: {
        title: 'Network Waiver',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs', route: '/NetworkAdequacyForPBMs' },
          { label: 'Network Waiver' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/NetworkWaiver.vue'),
    },
    {
      path: '/NetworkAdequacyForPBMs/PBMRentalAgreement',
      name: 'PBM Rental Agreement',
      meta: {
        title: 'PBM Rental Agreement',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs', route: '/NetworkAdequacyForPBMs' },
          { label: 'PBM Rental Agreement' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/PBMRentalAgreement.vue'),
    },
    {
      path: '/NetworkAdequacyForPBMs/ProviderFiles',
      name: 'Provider Files',
      meta: {
        title: 'Provider Files',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Network Adequacy For PBMs', route: '/NetworkAdequacyForPBMs' },
          { label: 'Provider Files' }
        ],
      },
      component: () => import('@/views/networkAdequacyForPBMs/ProviderFiles.vue'),
    },
// End - Network Adequacy for PBMs
    {
      path: '/AnnualRequiredFilingsAndForms',
      name: 'AnnualRequiredFilingsAndForms',
      meta: {
        title: 'Annual Required Filings And Forms',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Annual Required Filings And Forms' }
        ],
      },
      component: () => import('@/views/annualRequiredFilingsAndForms/AnnualRequiredFilingsAndForms.vue'),
    },
    {
      path: '/DocumentManagementTable',
      name: 'DocumentManagementTable',
      meta: {
        title: 'Document Management Table',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Document Management Table' }
        ],
      },
      component: () => import('@/views/documentManagement/DocumentManagementTable.vue'),
    },
    {
      path: '/HealthInsuranceLicensingUpdates',
      name: 'HealthInsuranceLicensingUpdates',
      meta: {
        title: 'Health Insurance Licensing Updates',
        requiresAuth: true
      },
      component: () => import('@/views/healthInsuranceLicensingUpdates/HealthInsuranceLicensingUpdates.vue'),
    },
    {
      path: '/QualityAssurance',
      name: 'QualityAssurance',
      meta: {
        title: 'Quality Assurance',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Quality Assurance' }
        ],
      },
      component: () => import('@/views/qualityAssurance/QualityAssurance.vue'),
    },
    {
      path: '/qualityAssurance/CreateExam',
      name: 'CreateExam',
      meta: {
        title: 'Create an Exam',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'Create an Exam' }
        ],
      },
      component: () => import('@/views/qualityAssurance/CreateExam.vue'),
    },
    {
      path: '/qualityAssurance/ReportingPhase',
      name: 'ReportingPhase',
      meta: {
        title: 'Reporting Phase',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'Reporting Phase' }
        ],
      },
      component: () => import('@/views/qualityAssurance/ReportingPhase.vue'),
    },
    {
      path: '/qualityAssurance/ExamManagementForQualityAssurance',
      name: 'ExamManagementForQualityAssurance',
      meta: {
        title: 'Exam Management for Quality Assurance',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'Exam Management for Quality Assurance' }
        ],
      },
      component: () => import('@/views/qualityAssurance/ExamManagementForQualityAssurance.vue'),
    },
    {
      path: '/qualityAssurance/OpeningDocumentsSampleFilesUpload',
      name: 'OpeningDocumentsSampleFilesUpload',
      meta: {
        title: 'Opening Documents/Sample Files Upload',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Opening Documents/Sample Files Upload' }
        ],
      },
      component: () => import('@/views/qualityAssurance/OpeningDocumentsSampleFilesUpload.vue'),
    },
    {
      path: '/qualityAssurance/QualityAssuranceFileUploadsByHmoCbp',
      name: 'QualityAssuranceFileUploadsByHmoCbp',
      meta: {
        title: 'File Uploads by HMO/CBP',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA', 'QA-HMO', 'QA-DHS', 'EntityAdministrator'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'File Uploads by HMO/CBP' }
        ],
      },
      component: () => import('@/views/qualityAssurance/QualityAssuranceFileUploadsByHmoCbp.vue'),
    },
    {
      path: '/qualityAssurance/ExamSummaryFindings',
      name: 'ExamSummaryFindings',
      meta: {
        title: 'Phase 2 - Exam Summary and Findings',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'Phase 2 - Exam Summary and Findings' }
        ],
      },
      component: () => import('@/views/qualityAssurance/ExamSummaryFindings.vue'),
    },
    {
      path: '/UploadsForQualityAssuranceExams',
      name: 'UploadsForQualityAssuranceExams',
      meta: {
        title: 'Uploads For Quality Assurance Exams',
        requiresAuth: true
      },
      component: () => import('@/views/uploadsForQualityAssuranceExams/UploadsForQualityAssuranceExams.vue'),
    },
    {
      path: '/qualityAssurance/QualityAssuranceExamDocuments',
      name: 'QualityAssuranceExamDocuments',
      meta: {
        title: 'Exam Documents',
        requiresAuth: true,
        requiresRoles: ['ADMIN', 'QA', 'QA-HMO', 'QA-DHS', 'EntityAdministrator'],
        breadcrumb: [
          { label: 'Quality Assurance', route: '/QualityAssurance' },
          { label: 'Exam Documents' }
        ],
      },
      component: () => import('@/views/qualityAssurance/QualityAssuranceExamDocuments.vue'),
    },
    {
      path: '/HMOTransactions',
      name: 'HMOTransactions',
      meta: {
        title: 'HMO Transactions',
        requiresAuth: true
      },
      component: () => import('@/views/hmoTransactions/HMOTransactions.vue'),
    },
    {
      path: '/UpdatesLicensingDocuments',
      name: 'UpdatesLicensingDocuments',
      meta: {
        title: 'Licensing Updates & Other Required Reporting',
        requiresAuth: true,
        breadcrumb: [
          { label: 'Licensing Updates & Other Required Reporting' }
        ],
      },
      component: () => import('@/views/updatesLicensingDocuments/UpdatesLicensingDocuments.vue'),
    },
 ]
});

router.beforeEach(async (to, from, next) => {
  const dialogStore = useDialogStore();
  dialogStore.close();
  handleNavigation();

  function handleNavigation() {
    if (to.meta.requiresAuth) {
      const store = useAuthStore();
      if (!store.authenticated) {
        next({ path: '/' });
      } else if (to.meta.requiresAdmin && !store.user.isAdminUser) {
        next({ path: '/unauthorized' });
      } else if (to.meta.requiresRoles) {
        // Check if user has any of the required roles
        const hasRequiredRole = to.meta.requiresRoles.some(role => store.hasRole(role));
        if (!hasRequiredRole) {
          next({ path: '/unauthorized' });
        } else {
          next();
        }
      } else {
        next();
      }
    } else {
      next();
    }
  }
});

export default router;
